import React, { useState } from 'react';
import MDBox from 'components/MDBox';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { StatusLabel } from './bookNewSessionTabUtils';
import { SlotGridItem } from './bookNewSessionTabStyled';
import { isBefore, isEqual, startOfDay } from 'date-fns';
import BookTutoringSessionV2Dialog from './BookTutoringSessionV2Dialog';
import { FormInputCheckbox } from 'components/Common/Forms/FormInputCheckbox';
import { EmptyState } from 'layouts/studentSuccessAdvocate/ssa/components/ssaStudentProfileTabs/SsaAttendanceTabComponent/SsaAtendanceTabUtils';

const TimeSlotsGrid = ({
  student,
  loadAllData,
  selectedDate,
  timeSlots = [],
  watchedSubject,
  isLoading = false,
  bookedSessions = [],
  overrideLimits = false,
}) => {
  const { control, setValue } = useForm({ mode: 'onChange' });

  const hasReachedDailyLimit = bookedSessions?.length >= 2;

  const [selectedSlot, setSelectedSlot] = useState(null);
  const [openBookSessionDialog, setOpenBookSessionDialog] = useState(false);

  const isPastOrToday = () => {
    const today = startOfDay(new Date());
    const slotDate = startOfDay(new Date(selectedDate));

    return isEqual(slotDate, today) || isBefore(slotDate, today);
  };

  const isBooked = (slot) =>
    bookedSessions.some((s) => s.timeSlotId === slot?.timeSlotId);

  const getSessionId = (slot) =>
    bookedSessions.find((s) => s.timeSlotId === slot?.timeSlotId)?.sessionId;

  const getLabel = (slot) => {
    if (!slot) return null;

    const time = `${slot?.startTimeFmt} - ${slot?.endTimeFmt}`;

    const bookedBySessionApi = isBooked(slot); // from sessions API
    const blockedByAvailability = slot?.available === false;

    const available = () => (
      <StatusLabel icon="✅" text={`Available: ${time}`} />
    );

    const unavailable = () => (
      <StatusLabel icon="❌" text={`Unavailable: ${time}`} />
    );

    // 1️⃣ REAL BOOKING → ALWAYS BOOKED
    if (bookedBySessionApi) {
      return <StatusLabel icon="🟡" text={`BOOKED: ${time}`} />;
    }

    // 2️⃣ API says not available (overlap / tutor rule)
    if (blockedByAvailability) {
      return unavailable();
    }

    if (overrideLimits) {
      return available();
    }

    if (hasReachedDailyLimit || isPastOrToday()) {
      return unavailable();
    }

    return available();
  };

  const isDisabled = (slot) => {
    const bookedBySessionApi = isBooked(slot);
    const isTodayOrPast = isPastOrToday();

    if (overrideLimits) {
      return false;
    }

    if (isTodayOrPast) {
      return !bookedBySessionApi;
    }
    if (bookedBySessionApi) return false;
    if (hasReachedDailyLimit) return true;

    return false;
  };

  const handleChange = (slot, checked) => {
    setSelectedSlot(slot);
    setOpenBookSessionDialog(true);
  };

  const handleDialogClose = () => {
    // Uncheck checkbox
    if (selectedSlot?.timeSlotId) {
      setValue(selectedSlot.timeSlotId, false);
    }

    setSelectedSlot(null);
    setOpenBookSessionDialog(false);
  };

  if (!isLoading && (!timeSlots || timeSlots.length === 0)) {
    return (
      <>
        <Grid container item spacing={1}>
          <Grid item xs={12}>
            <EmptyState message="No time slots configured for this subject." />
          </Grid>
        </Grid>
        <MDBox mb={4} />
      </>
    );
  }

  return (
    <>
      <Grid container spacing={1}>
        {timeSlots?.map((slot) => (
          <SlotGridItem item xs={12} sm={6} md={4} key={slot?.timeSlotId}>
            <FormInputCheckbox
              control={control}
              label={getLabel(slot)}
              name={slot?.timeSlotId}
              readOnly={isDisabled(slot)}
              onValueChange={(checked) => handleChange(slot, checked)}
            />
          </SlotGridItem>
        ))}
      </Grid>
      <MDBox mb={4} />
      <BookTutoringSessionV2Dialog
        student={student}
        slot={selectedSlot}
        loadAllData={loadAllData}
        subjectId={watchedSubject}
        selectedDate={selectedDate}
        open={openBookSessionDialog}
        overrideLimits={overrideLimits}
        isBooked={isBooked(selectedSlot)}
        handleDialogClose={handleDialogClose}
        sessionId={getSessionId(selectedSlot)}
      />
    </>
  );
};

export default TimeSlotsGrid;
